import { FilterConfig, useFiltering } from "hooks/useFiltering";
import { FeatureRequestListItem } from "interfaces/feature-request";
import React, { useEffect } from "react";
import TableFilters from "./TableFilters";
import TableRow from "./TableRow";
import { useHistory, useLocation } from "react-router-dom";
import Table, { TableColumn } from "../shared/Table";
import { Container, Header } from "../shared/generic";
import { useTranslate } from "hooks/common/useTranslate/useTranslate";
import { useScrollRestoration } from "hooks/common/useScrollRestoration/useScrollRestoration";
import PriorityBars from "../shared/icons/PriorityBars";
import styled from "styled-components";
import moment from "moment";

const filterConfig: { [key: string]: FilterConfig<FeatureRequestListItem> } = {
    search: { fields: ["featureRequestId", "name", "description"], type: "string" },
    state: { fields: ["state"], type: "exact" },
    priority: { fields: ["datahubPriority"], type: "exact" },
    businessArea: { fields: ["businessArea"], type: "string" },
    version: { fields: ["dhVersion"], type: "exact" },
    meeting: { fields: ["nextOrLastMeetingDate"], type: "exact" },
};

const Wrapper = styled.div<{ visible: boolean }>`
    display: ${(props) => (props.visible ? "block" : "none")};
`;

const SmallHeaderText = styled.div`
    font-size: 12px;
`;

const ListPage: React.FC<{
    featureRequests: FeatureRequestListItem[];
    visible: boolean;
}> = ({ featureRequests, visible }) => {
    const { scrollToTop, scrollToPrevious } = useScrollRestoration();
    const { filteredData, getFilterValue, setFilterValue, resetFilters } =
        useFiltering<FeatureRequestListItem>(featureRequests, filterConfig);
    const translate = useTranslate();
    const history = useHistory();
    const location = useLocation();

    const columns: TableColumn<FeatureRequestListItem>[] = [
        {
            key: "updatedAt",
            header: translate("featureRequests.featureRequestsTable.updated"),
            sortable: true,
        },
        {
            key: "featureRequestId",
            header: translate("featureRequests.featureRequestsTable.id"),
            sortable: true,
        },
        {
            key: "name",
            header: translate("featureRequests.featureRequestsTable.name"),
            sortable: true,
        },
        {
            key: "state",
            header: translate("featureRequests.featureRequestsTable.state"),
            sortable: true,
        },
        {
            key: "businessArea",
            header: translate("featureRequests.featureRequestsTable.businessArea"),
            sortable: true,
        },
        {
            key: "nextOrLastMeetingDate",
            header: translate("featureRequests.featureRequestsTable.meeting"),
            sortable: true,
            render: (item: FeatureRequestListItem) => {
                if (!item.nextOrLastMeetingDate) {
                    return null;
                }

                return <div>{moment(item.nextOrLastMeetingDate).format("DD.MM.YYYY")}</div>;
            },
        },
        {
            key: "dhVersion",
            header: translate("featureRequests.featureRequestsTable.dhVersion"),
            sortable: true,
            render: (item: FeatureRequestListItem) => {
                if (!item.dhVersion) {
                    return <div>-</div>;
                }

                return (
                    <div>
                        <div>{item.dhVersion}</div>
                        {item.dhVersionType === "PLANNED" && (
                            <SmallHeaderText>
                                ({translate("featureRequests.dhVersion.planned")})
                            </SmallHeaderText>
                        )}
                    </div>
                );
            },
        },
        {
            key: "datahubPriority",
            header: translate("featureRequests.featureRequestsTable.datahubPriority"),
            sortable: true,
            render: (item: FeatureRequestListItem) => (
                <PriorityBars priority={item.datahubPriority} />
            ),
        },
    ];

    const handleRowClick = (item: FeatureRequestListItem) => {
        scrollToTop();
        history.push(`${location.pathname}?featureRequestId=${item.id}`);
    };

    useEffect(() => {
        if (visible) {
            scrollToPrevious();
        }
    }, [visible, scrollToPrevious]);

    return (
        <Wrapper visible={visible}>
            <Header>{translate("featureRequests.featureRequestsList.title")}</Header>
            <p>{translate("featureRequests.featureRequestsList.description")}</p>
            <Container>
                <TableFilters
                    getFilterValue={getFilterValue}
                    setFilterValue={setFilterValue}
                    resetFilters={resetFilters}
                    featureRequests={featureRequests}
                />
                <Table
                    data={filteredData}
                    columns={columns.map((col) => ({
                        ...col,
                        render:
                            col.render ||
                            ((item: FeatureRequestListItem) => (
                                <TableRow
                                    valueType={col.key as keyof FeatureRequestListItem}
                                    item={item}
                                />
                            )),
                    }))}
                    initialOrderBy="updatedAt"
                    onRowClick={handleRowClick}
                />
            </Container>
        </Wrapper>
    );
};

export default ListPage;
